'use client';

/**
 * React Query hooks for translations feature.
 *
 * Provides data fetching and mutation hooks with caching,
 * automatic invalidation, and error handling.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  fetchTranslations,
  fetchTranslation,
  createTranslation,
  updateTranslation,
  deleteTranslation,
  bulkDeleteTranslations,
  addComment,
  uploadGalleryImage,
  removeGalleryImage,
  fetchMentionableUsers,
  fetchTranslationNotifications,
  updateTranslationKey,
  updateTranslationTags,
  updateTranslationDescription,
  fetchTagsByKey,
  searchTags,
  forceReleaseTranslation,
  bulkForceReleaseTranslations,
  previewImport,
  confirmImport,
} from './api';
import type {
  BulkDeleteFilter,
  BulkDeleteResult,
  BulkForceReleaseResult,
} from './api';
import type {
  TranslationFilters,
  CreateTranslationPayload,
  UpdateTranslationPayload,
  AddCommentPayload,
  UpdateKeyPayload,
  UpdateTagsPayload,
  UpdateDescriptionPayload,
  ConfirmImportPayload,
} from './types';
import { isTransientApiError } from '@/infrastructure/api/error';
import { useToast } from '@/infrastructure/hooks';

/**
 * Query key factory for translations.
 * Ensures consistent key generation for cache operations.
 */
export const translationKeys = {
  all: ['translations'] as const,
  lists: () => [...translationKeys.all, 'list'] as const,
  list: (filters: TranslationFilters) =>
    [...translationKeys.lists(), filters] as const,
  details: () => [...translationKeys.all, 'detail'] as const,
  detail: (uuid: string) => [...translationKeys.details(), uuid] as const,
  mentionableUsers: (uuid: string) =>
    [...translationKeys.all, 'mentionable-users', uuid] as const,
  notifications: (uuid: string) =>
    [...translationKeys.all, 'notifications', uuid] as const,
};

/**
 * Query options for fetching a paginated list of translations.
 * Can be used with prefetching or direct query calls.
 */
export function translationsQueryOptions(filters: TranslationFilters = {}) {
  return queryOptions({
    queryKey: translationKeys.list(filters),
    queryFn: () => fetchTranslations(filters),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
}

/**
 * Query options for fetching a single translation by UUID.
 */
export function translationQueryOptions(
  uuid: string,
  includeDeleted?: boolean
) {
  return queryOptions({
    queryKey: translationKeys.detail(uuid),
    queryFn: () => fetchTranslation(uuid, includeDeleted),
    staleTime: 60 * 1000, // 1 minute
    enabled: !!uuid,
  });
}

/**
 * Hook to fetch a paginated list of translations.
 *
 * @param filters - Filter, pagination, and sorting options
 * @returns Query result with translations page data
 *
 * @example
 * const { data, isLoading, error } = useTranslations({
 *   page: 1,
 *   pageSize: 20,
 *   locale: 'en-US',
 *   search: 'login'
 * });
 */
export function useTranslations(filters: TranslationFilters = {}) {
  return useQuery(translationsQueryOptions(filters));
}

/**
 * Hook to fetch a single translation by UUID.
 *
 * @param uuid - Translation UUID
 * @param includeDeleted - Include soft-deleted translations
 * @returns Query result with translation detail data
 *
 * @example
 * const { data, isLoading } = useTranslation('uuid-here');
 */
export function useTranslation(uuid: string, includeDeleted?: boolean) {
  return useQuery(translationQueryOptions(uuid, includeDeleted));
}

/**
 * Hook to create a new translation.
 *
 * Automatically invalidates the translations list cache on success.
 *
 * @example
 * const mutation = useCreateTranslation();
 * mutation.mutate({
 *   key: 'auth.login.button',
 *   value: 'Sign In',
 *   locale: 'en-US',
 *   domain: 'UI'
 * });
 */
export function useCreateTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTranslationPayload) =>
      createTranslation(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: translationKeys.details() });

      // Invalidate tags-by-key cache so "Add missing translation" picks up
      // the tags from the just-created translation instead of stale empty result
      if (payload.tags?.length) {
        queryClient.invalidateQueries({
          queryKey: [...translationKeys.all, 'tags-by-key', payload.key],
        });
      }
    },
  });
}

/**
 * Hook to update an existing translation.
 *
 * Automatically invalidates both the list and detail caches on success.
 *
 * @example
 * const mutation = useUpdateTranslation();
 * mutation.mutate({
 *   uuid: 'translation-uuid',
 *   payload: { value: 'Updated Value', changeNote: 'Fixed typo' }
 * });
 */
export function useUpdateTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: UpdateTranslationPayload;
    }) => updateTranslation(uuid, payload),
    onSuccess: (_data, { uuid, payload }) => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });

      if (payload.tags) {
        // Tags are propagated cross-key by the backend — invalidate all detail caches
        queryClient.invalidateQueries({
          queryKey: translationKeys.details(),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: translationKeys.detail(uuid),
        });
      }
    },
  });
}

/**
 * Hook to delete a translation.
 *
 * Automatically invalidates the translations list cache on success.
 *
 * @example
 * const mutation = useDeleteTranslation();
 * mutation.mutate('translation-uuid');
 */
export function useDeleteTranslation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (uuid: string) => deleteTranslation(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
    },
    // Surface the backend reason (404 not found, 403 forbidden state/ownership,
    // 500) instead of swallowing it — the dialog stays open so the user can retry.
    onError: (error) => {
      toast.error(
        error instanceof Error && error.message
          ? `Translation not deleted: ${error.message}`
          : 'Translation not deleted. Check your connection and try again.'
      );
    },
  });
}

/**
 * Hook to bulk-delete translations restricted to `state = Draft`.
 *
 * Accepts either an explicit list of UUIDs or a filter object — the server
 * enforces draft-only and returns a `{ deleted, deletedUuids, skipped }`
 * summary so the UI can render a skip toast.
 *
 * @example
 * const mutation = useBulkDeleteDraftTranslations();
 * const result = await mutation.mutateAsync({ filter: { source: 'Figma' } });
 * // result.skipped: [{ uuid, reason: 'not_draft' }, ...]
 */
export function useBulkDeleteDraftTranslations() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkDeleteResult,
    Error,
    { uuids?: string[]; filter?: BulkDeleteFilter }
  >({
    mutationFn: (payload) => bulkDeleteTranslations(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
    },
  });
}

/**
 * Transport-level failures (timeout, offline, DNS) are wrapped by `apiClient`
 * into `ApiTransportError` — the transient case worth retrying. HTTP 4xx/5xx
 * surface as a plain `Error` and must NOT be retried, so a
 * validation/permission failure isn't hammered. Delegates to the shared
 * `isTransientApiError` guard instead of `instanceof TypeError`, which stopped
 * matching once the client started wrapping fetch rejections.
 */
function isTransientNetworkError(error: unknown): boolean {
  return isTransientApiError(error);
}

/**
 * Hook to add a comment to a translation.
 *
 * The comment is rendered only after the server confirms it was persisted (no
 * optimistic insert): a failed or aborted request must never look saved.
 * Transient connection drops are retried, and any final failure is surfaced via
 * a toast so the comment is never lost silently (bug 23421).
 */
export function useAddComment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: AddCommentPayload;
    }) => addComment(uuid, payload),
    // Bounded retry for transient connection blips only; 4xx/5xx are not retried.
    retry: (failureCount, error) =>
      failureCount < 2 && isTransientNetworkError(error),
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    onError: (error) => {
      toast.error(
        error instanceof Error && error.message
          ? `Comment not saved: ${error.message}`
          : 'Comment not saved. Check your connection and try again.'
      );
    },
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: translationKeys.detail(uuid),
      });
    },
  });
}

/**
 * Hook to upload a visual-context image to a translation's gallery.
 *
 * Any final failure is surfaced via a toast so an upload is never lost
 * silently (mirrors useAddComment; bugs 23421/23871 came from silent
 * mutations). Invalidates the detail cache on success so the new image (with
 * its presigned URL) renders immediately.
 */
export function useUploadGalleryImage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ uuid, file }: { uuid: string; file: File }) =>
      uploadGalleryImage(uuid, file),
    onError: (error) => {
      toast.error(
        error instanceof Error && error.message
          ? `Image not uploaded: ${error.message}`
          : 'Image not uploaded. Check your connection and try again.'
      );
    },
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: translationKeys.detail(uuid),
      });
    },
  });
}

/**
 * Hook to remove a visual-context image from a translation's gallery.
 *
 * Failures are surfaced via a toast (never silent); the detail cache is
 * invalidated on success so the thumbnail disappears immediately.
 */
export function useRemoveGalleryImage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ uuid, fileUuid }: { uuid: string; fileUuid: string }) =>
      removeGalleryImage(uuid, fileUuid),
    onError: (error) => {
      toast.error(
        error instanceof Error && error.message
          ? `Image not removed: ${error.message}`
          : 'Image not removed. Check your connection and try again.'
      );
    },
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({
        queryKey: translationKeys.detail(uuid),
      });
    },
  });
}

/**
 * Hook to fetch the @-mention roster for a translation's comments.
 *
 * Lazy-friendly: pass `enabled = false` to defer the request until the
 * mention picker actually needs the roster (e.g. only when the user can
 * comment, or on first `@` keystroke). The roster rarely changes, so it is
 * cached for a few minutes.
 *
 * @param uuid - Translation UUID
 * @param enabled - When `false`, the query is not executed (default `true`)
 */
export function useMentionableUsers(uuid: string, enabled = true) {
  return useQuery({
    queryKey: translationKeys.mentionableUsers(uuid),
    queryFn: () => fetchMentionableUsers(uuid),
    enabled: !!uuid && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch the notification-dispatch history for a translation.
 *
 * Each entry is one workflow event that fanned out a notification (state
 * changes, change requests, comments/mentions, bulk actions), with its
 * recipients — sorted newest-first by the analytics service. The audit is
 * forward-looking, so an empty list is the normal initial state.
 *
 * Mirrors `useTranslation`'s key/staleTime conventions (1-minute staleTime,
 * disabled until a uuid is present).
 *
 * @param uuid - Translation UUID
 */
export function useTranslationNotifications(uuid: string) {
  return useQuery({
    queryKey: translationKeys.notifications(uuid),
    queryFn: () => fetchTranslationNotifications(uuid),
    enabled: !!uuid,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to update a translation's key.
 */
export function useUpdateTranslationKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: UpdateKeyPayload;
    }) => updateTranslationKey(uuid, payload),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: translationKeys.detail(uuid),
      });
    },
  });
}

/**
 * Hook to update a translation's tags.
 *
 * Tags are metadata and editable in any state; this never triggers a workflow
 * state transition. Invalidates the affected detail plus all lists (tags are
 * replicated across related locales by the backend).
 */
export function useUpdateTranslationTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: UpdateTagsPayload;
    }) => updateTranslationTags(uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
      // Tags are replicated across every locale of the key by the backend, so
      // invalidate ALL detail caches (not just the edited uuid) to avoid stale
      // tags lingering on sibling-locale detail views.
      queryClient.invalidateQueries({ queryKey: translationKeys.details() });
    },
  });
}

/**
 * Hook to update a translation's description (any state, no workflow
 * transition). The description is per locale, so only the edited detail is
 * invalidated.
 */
export function useUpdateTranslationDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: UpdateDescriptionPayload;
    }) => updateTranslationDescription(uuid, payload),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: translationKeys.detail(uuid) });
    },
  });
}

/**
 * Hook to look up tags by translation key.
 *
 * Used in the create form to auto-fill tags when the user types a key
 * that already has translations in other locales.
 *
 * @param key - The translation key to look up (must be at least 3 chars)
 * @returns Query result with tags array
 */
export function useTagsByKey(key: string) {
  return useQuery({
    queryKey: [...translationKeys.all, 'tags-by-key', key] as const,
    queryFn: () => fetchTagsByKey(key),
    enabled: key.length >= 3,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to search distinct tags across all translations.
 *
 * The caller is expected to debounce the `search` string; this hook does not
 * debounce internally, so every change triggers a new query key (with caching).
 *
 * @param search - Case-insensitive substring to match. Empty string returns top tags.
 * @param limit - Max number of tags to return (default 10).
 */
export function useSearchTags(search: string, limit = 10) {
  return useQuery({
    queryKey: [...translationKeys.all, 'tags-search', search, limit] as const,
    queryFn: () => searchTags(search, limit),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to force-release a translation, bypassing the reviewer-approval flow.
 *
 * Invalidates the translation detail and list caches on success so the new
 * Released state is reflected immediately. Workflow queues are also
 * invalidated through `translationKeys.all` so review queues drop the entry.
 *
 * Caller must hold the `i18n:force-release` permission; the backend rejects
 * unauthorized requests with 403.
 *
 * @example
 * const forceRelease = useForceReleaseTranslation();
 * forceRelease.mutate({ uuid, payload: { reason: 'Hotfix' } });
 */
export function useForceReleaseTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload?: { reason?: string };
    }) => forceReleaseTranslation(uuid, payload ?? {}),
    onSuccess: (_data, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: translationKeys.all });
      queryClient.invalidateQueries({
        queryKey: translationKeys.detail(uuid),
      });
      // Workflow queues are keyed under ['workflow', ...]; invalidate the
      // whole namespace so review/pre-review queues drop this UUID.
      queryClient.invalidateQueries({ queryKey: ['workflow'] });
    },
  });
}

/**
 * Hook to bulk force-release translations, bypassing the reviewer-approval
 * flow for every UUID in the payload.
 *
 * Selection-based only (v1): caller passes an explicit list of UUIDs and a
 * required reason. Server enforces the `i18n:force-release` permission and
 * skips rows already in `Released` or `Deprecated`, returning a structured
 * `{ released, releasedUuids, skipped[] }` summary so the UI can render a
 * skip toast.
 *
 * Invalidates translations + workflow caches on success so review/pre-review
 * queues drop the released UUIDs immediately.
 *
 * @example
 * const mutation = useBulkForceReleaseTranslations();
 * const result = await mutation.mutateAsync({ uuids, reason: 'Hotfix' });
 * // result.skipped: [{ uuid, reason: 'already_released' }, ...]
 */
export function useBulkForceReleaseTranslations() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkForceReleaseResult,
    Error,
    { uuids: string[]; reason: string }
  >({
    mutationFn: (payload) => bulkForceReleaseTranslations(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationKeys.all });
      // Workflow queues are keyed under ['workflow', ...]; invalidate so
      // review/pre-review queues drop the released UUIDs.
      queryClient.invalidateQueries({ queryKey: ['workflow'] });
    },
  });
}

/**
 * Hook to preview an import file.
 */
export function usePreviewImport() {
  return useMutation({
    mutationFn: (file: File) => previewImport(file),
  });
}

/**
 * Hook to confirm an import.
 *
 * A bulk import can create or update an arbitrary number of translations
 * across many keys, locales and states, so we invalidate broadly:
 * - All translation queries (lists, details, tags-by-key, tags-search) — the
 *   detail page reads sibling-locale state from its own cache, so without
 *   `details()` it would keep showing "Missing" until the staleTime expired.
 * - Workflow queues — newly imported drafts may surface in pre-review.
 * - Dashboard stats/queues/charts — counts shift after every import.
 */
export function useConfirmImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ConfirmImportPayload) => confirmImport(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['workflow'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
